/**
 * buildSelectedProductsForDialog — extraído de ForecastPage.tsx (P7 split).
 *
 * Construye el array de productos a pasar a SelectedProductsDialog:
 *   - Filtra activeForPurchase=true.
 *   - Mapea a la shape del diálogo con sellingPrice resuelto Odoo18→Odoo16.
 *
 * Función pura: la página la envuelve en useMemo. NO descarta productos por
 * demanda neta — un producto con inventario suficiente para el horizonte
 * regular puede aún tener muestras de distribución pendientes que justifican
 * incluirlo en el draft. El backend decide la cantidad final por línea.
 */

import { ProductData } from "./types";
import { SelectedProduct } from "../../components/Forecast/SelectedProducts.types";

export function buildSelectedProductsForDialog(
  products: ProductData[],
  selectedIds: Set<string>,
  _forecastMonths: number,
  horizonAddedIds: Set<string>,
): SelectedProduct[] {
  if (selectedIds.size === 0) return [];
  const selectedMap = new Set(selectedIds);
  return products
    .filter((p) => {
      if (!selectedMap.has(p.unifiedProductId)) return false;
      if (p.activeForPurchase === false) return false;
      return true;
    })
    .map((p) => ({
      unifiedProductId: p.unifiedProductId,
      unifiedName: p.unifiedName,
      unifiedCode: p.unifiedCode ?? null,
      addedByHorizon: horizonAddedIds.has(p.unifiedProductId),
      totalAvailableQty: p.totalAvailableQty,
      purchaseCant: p.purchaseCant ?? null,
      purchaseCantUser: p.purchaseCantUser ?? null,
      sellingPrice: (() => {
        // Bloque 4 (plan costo_margen_real_iva): precio de lista CON IVA. El
        // list_price de Odoo es neto; se aplica el factor de IVA del producto
        // (costo c/IVA ÷ costo neto), lo que mantiene el ratio margen/costo
        // invariante al IVA. Sin costo neto conocido → list_price tal cual.
        const listPrice =
          p?.odoo18 && typeof p.odoo18.list_price === "number"
            ? p.odoo18.list_price
            : p?.odoo16 && typeof p.odoo16.list_price === "number"
              ? p.odoo16.list_price
              : null;
        if (listPrice == null) return null;
        const ivaFactor =
          typeof p.lastPurchaseCost === "number" &&
          typeof p.lastPurchaseCostNet === "number" &&
          p.lastPurchaseCostNet > 0
            ? p.lastPurchaseCost / p.lastPurchaseCostNet
            : 1;
        return listPrice * ivaFactor;
      })(),
      existsInOdoo18: p.existsInOdoo18,
      existsInOdoo16: p.existsInOdoo16,
      odoo18Id: p?.odoo18?.id ?? null,
      odoo16Id: p?.odoo16?.id ?? null,
      // Keep cost rule aligned with supplier cards: only canonical last_purchase_cost counts.
      lastPurchaseCost:
        typeof p.lastPurchaseCost === "number" && p.lastPurchaseCost > 0
          ? p.lastPurchaseCost
          : null,
      // Bloque C.6 — métricas back-test del Bloque A.5 expuestas al dialog
      // (persistidas en product_mapping por generateForecasts).
      peakMonthlySales:
        typeof (p as any).peakMonthlySales === "number"
          ? (p as any).peakMonthlySales
          : null,
      lowMonthlySales:
        typeof (p as any).lowMonthlySales === "number"
          ? (p as any).lowMonthlySales
          : null,
      lostSalesAtPeak:
        typeof (p as any).lostSalesAtPeak === "number"
          ? (p as any).lostSalesAtPeak
          : null,
      lostSalesCurrency:
        typeof (p as any).lostSalesCurrency === "string"
          ? (p as any).lostSalesCurrency
          : "MXN",
    })) as SelectedProduct[];
}
